import {Component, DestroyRef, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {DetailsConfig, SharedUiDetailsModule} from '@snelstart/ui-details';
import {
  BokkzAlgDataService,
  BokkzClient,
  BokkzWageDataService,
  BokkzWageLogic, ClientEntity,
  LoonAangifteDto,
  LoonstaatEntity,
  PdfRequest, PdfRequestOptie, PdfRequestSoort, PdfRequestType,
  VerloningsPeriode, WerkgeverEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {ToastService} from "@snelstart/ui-toast";
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ButtonConfiguration, SharedUiButtonModule} from '@snelstart/ui-button';
import {filter, Observable, shareReplay, Subscriber, tap} from 'rxjs';
import {WerknemerEntity} from "@snelstart-ventures/fiscaal/domain-logic";
import {SharedUiTabModule} from '@snelstart/ui-tab';
import {DienstverbandComponent} from '../dienstverband/dienstverband.component';
import {SalarisstrookComponent} from '../salarisstrook/salarisstrook.component';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {CommonModule} from '@angular/common';
import {ContractComponent} from "../contract/contract.component";
import {RoosterComponent} from "../rooster/rooster.component";
import {FiscaalComponent} from "../fiscaal/fiscaal.component";
import {VastevergoedingComponent} from "../vastevergoeding/vastevergoeding.component";
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiSearchControlModule} from '@snelstart/ui-search-control';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-tax-wage-details',
  templateUrl: './feature-tax-wage-details.component.html',
  styleUrl: './feature-tax-wage-details.component.scss',
  imports: [
    SharedUiButtonModule,
    SharedUiDetailsModule,
    SharedUiTabModule,
    DienstverbandComponent,
    SalarisstrookComponent,
    SharedUiDialogModule,
    CommonModule,
    ContractComponent,
    RoosterComponent,
    FiscaalComponent,
    VastevergoedingComponent,
    ReactiveFormsModule,
    SharedUiFormControlsModule,
    SharedUiSearchControlModule
  ]
})
export class FeatureTaxWageDetailsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public id!: string;
  public employeeId: any;
  private sstRelatieId: any;
  private readonly destroyRef = inject(DestroyRef);
  private readonly algDataService = inject(BokkzAlgDataService);
  private readonly dataService = inject(BokkzWageDataService);
  private readonly logicService = inject(BokkzWageLogic);
  private readonly bokkzClient = inject(BokkzClient);
  private readonly toastService = inject(ToastService);

  private werkgeverEntity  = {} as WerkgeverEntity;
  private werknemerEntity = {} as WerknemerEntity;
  private loonstaatEntity = {} as LoonstaatEntity;
  private previousInputId?: string;

  public dialogSalSpecVisible = false;
  public selectedTab = 0;
  public selectedTabDv = 0;
  public selectedPeriode = 0;

  public detailsConfig = DetailsConfig.create({
    hideArrowButtons: true,
  });

  public afdrukkenForm = new FormGroup({
    conceptWatermerk: new FormControl<boolean>(false),
  });

  readonly clientEntity$: Observable<ClientEntity> =
    this.algDataService.clientEntity$.pipe(
      filter((data): data is ClientEntity => !!data?.sstRelationId),
      tap(data => {
        this.sstRelatieId = data.sstRelationId;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werkgeverEntity$: Observable<WerkgeverEntity> =
    this.dataService.werkgeverEntity$.pipe(
      filter((data): data is WerkgeverEntity => !!data?.id),
      tap(data => {
        this.werkgeverEntity = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      tap(data => {
        this.werknemerEntity = data;
        this.employeeId = data.id;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly loonstaatEntity$: Observable<LoonstaatEntity> =
    this.dataService.loonstaatEntity$.pipe(
      filter((data): data is LoonstaatEntity => !!data?.loonStaat),
      tap(data => {
        this.loonstaatEntity = data;
        this.employeeId = data.id;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedPeriode$: Observable<number> =
    this.dataService.selectedPeriode$.pipe(
      filter((data): data is number => !!data),
      tap(data => {
        this.selectedPeriode = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly selectedTab$: Observable<number> =
    this.dataService.selectedTab$.pipe(
      filter((data): data is number => !!data),
      tap(data => {
        this.selectedTab = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.clientEntity$.subscribe();
    this.werkgeverEntity$.subscribe();
    this.werknemerEntity$.subscribe();
    this.loonstaatEntity$.subscribe();
    this.selectedTab$.subscribe();
    this.selectedPeriode$.subscribe();
  }

  ngOnDestroy(): void {
    this.persistWerknemer();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const idChange = changes['id'];
    if (!idChange) {
      return;
    }

    const nextId = idChange.currentValue as string | undefined;
    const prevId = this.previousInputId;

    if (prevId && nextId && prevId !== nextId) {
      this.persistWerknemer();
    }

    if (nextId) {
      this.previousInputId = nextId;
    }
  }

  readonly saveButtonConfig = ButtonConfiguration.create({
    onClick: () =>
      new Observable<void>((subscription) => {
        this.saveForm(subscription);
      }),
  });

  configSettings(index: number) {
    return ButtonConfiguration.create({
      onClick: () =>
        new Observable((subscription) => {
          setTimeout(() => {
            subscription.next(true);
            subscription.complete();
            this.dataService.updateSelectedTab(index);
          }, 200);
        }),
    })
  }

  showDialogSalSpec() {
    this.dialogSalSpecVisible = true
  }

  onGeneratePdfClick() {
    if (!this.werknemerEntity.werknemerGegevens || !this.loonstaatEntity.loonStaat) {
      return;
    }
    this.genereerSalarisSpecificatie();
  }

  onSelectPeriode(number: number) {
    this.dataService.updateSelectedPeriode(number)
  }

  onSelectTabDv(number: number) {
    this.selectedTabDv = number;
  }

  private persistWerknemer(): void {
    if (this.werknemerEntity.werknemerGegevens?.dienstverbanden?.[0]) {
      this.werknemerEntity.werknemerGegevens.dienstverbanden[0].loonstaat = this.loonstaatEntity;
    }
    this.logicService.postWerknemerEntity(this.sstRelatieId, this.werknemerEntity);
  }

  saveForm(subscription?: Subscriber<void>): void {
    this.persistWerknemer();
    subscription?.next();
    subscription?.complete();
  }

  genereerSalarisSpecificatie() {
    const loonAangifteDto = {} as LoonAangifteDto;
    loonAangifteDto.werkgever = this.werkgeverEntity.werkgeverGegevens;
    // loonAangifteDto.aangifteTijdvak = this.loonstaatEntity.loonStaat?.verloningsPeriode?.find(
    //   (loonperiode) => loonperiode.periodenummer === this.selectedPeriode) as VerloningsPeriode;

    const geselecteerdeVerloningsPeriode = this.loonstaatEntity.loonStaat?.verloningsPeriode?.find(
        (loonperiode) => loonperiode.periodenummer === this.selectedPeriode,
    );

      loonAangifteDto.aangifteTijdvak = geselecteerdeVerloningsPeriode?.periode;
    loonAangifteDto.werknemerList = [];
    loonAangifteDto.werknemerList.push(this.werknemerEntity.werknemerGegevens!);

    const pdf = {} as PdfRequest;
    pdf.loonAangifteDto = loonAangifteDto;
    pdf.type = PdfRequestType.LB;
    pdf.soort = PdfRequestSoort.Werknemer;
    pdf.optie = PdfRequestOptie.Salarisspecificatie;
    pdf.run = 1;
    pdf.loonPeriode = this.selectedPeriode;
    pdf.personeelsNummer = this.werknemerEntity.werknemerGegevens?.persoon?.personeelsnummer ?? "0";
    pdf.concept = true;
    pdf.conceptWatermerk = this.afdrukkenForm.get('conceptWatermerk')?.value === true;
    pdf.time = new Date().toISOString();

    const pdfAntwoord = this.bokkzClient.getPdfRapport(pdf);
    pdfAntwoord.subscribe({
      next: (pdfStringBase64) => {
        const datum = new Date(pdf.time!);
        const formattedDatum = new Intl.DateTimeFormat('nl-NL', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }).format(datum);

        let blob = {} as Blob;
        try {
          blob = this.dataURItoBlob(pdfStringBase64);
        } catch (error) {
          this.toastService.showErrorMessage('Document is niet beschikbaar  ' + error);
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        const naam = (this.werknemerEntity.werknemerGegevens?.persoon?.voorletters ?? '') + ' ' +
          (this.werknemerEntity.werknemerGegevens?.persoon?.tussenvoegsel ?? '') + ' ' +
          (this.werknemerEntity.werknemerGegevens?.persoon?.achternaam ?? '').trim();

        const year = this.loonstaatEntity.loonStaat?.year ?? new Date().getFullYear();

        if (naam !== null) {
          link.download = 'Salaris ' + this.selectedPeriodeSalaris() + ' ' + year + ' - ' + naam + ' (' + formattedDatum + ').pdf';
        } else {
          link.download = 'Salaris.pdf';
        }

        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Cleanup: verwijder link element
        URL.revokeObjectURL(url); // Cleanup: vrijgeven van URL object

        this.toastService.showSuccessMessage('Het pdf-document is als download beschikbaar.');
      },
      error: (error) => {
        console.log('error', error);
        this.toastService.showErrorMessage('Het pdf-document is helaas niet beschikbaar. Dit kan verschillende oorzaken hebben. We hebben onze IT-afdeling hierover geïnformeerd. Probeer het later nog eens. Blijft het probleem zich voordoen? Neem dan contact op met onze Klantenservice.');
      },
    });
  }

  dataURItoBlob(dataURI: string) {
    if (dataURI != null) {
      const byteString = atob(dataURI.replace(/"/g, ''));
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      return new Blob([int8Array], {type: 'application/pdf'});
    } else throw 'No dataobject';
  }

  selectedPeriodeSalaris() {
    const maanden = [
      '',
      'Januari',
      'Februari',
      'Maart',
      'April',
      'Mei',
      'Juni',
      'Juli',
      'Augustus',
      'September',
      'Oktober',
      'November',
      'December',
    ];
    return maanden[this.selectedPeriode] || '';
  }

}
